import { useState } from 'react';
import { Mic, Sparkles, ArrowRight, CheckCircle2, Loader2, Play } from 'lucide-react';
import { supabase } from '../lib/supabase';

interface DemoModalProps {
  onClose: () => void;
}

export default function DemoModal({ onClose }: DemoModalProps) {
  const [step, setStep] = useState<'intro' | 'form' | 'recording' | 'processing' | 'results'>('intro');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    jobTitle: ''
  });
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [isRecording, setIsRecording] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [answers, setAnswers] = useState<string[]>([]);

  const questions = [
    "What would you say are your top three professional strengths?",
    "Can you describe a time when you made a significant impact on a project or team?",
    "How would your colleagues describe your work style and collaboration approach?"
  ];

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setStep('recording');
  };

  const startRecording = () => {
    setIsRecording(true);
    setRecordingTime(0);
    const interval = setInterval(() => {
      setRecordingTime(prev => {
        if (prev >= 60) {
          clearInterval(interval);
          stopRecording();
          return 60;
        }
        return prev + 1;
      });
    }, 1000);
  };

  const stopRecording = () => {
    setIsRecording(false);
    const mockAnswer = `Mock answer for question ${currentQuestion + 1}`;
    setAnswers([...answers, mockAnswer]);
    
    if (currentQuestion < questions.length - 1) {
      setTimeout(() => {
        setCurrentQuestion(currentQuestion + 1);
        setRecordingTime(0);
      }, 500);
    } else {
      setTimeout(async () => {
        setStep('processing');
        
        // Save demo lead to database
        try {
          await supabase.from('demo_leads').insert({
            name: formData.name,
            email: formData.email,
            company: formData.company,
            job_title: formData.jobTitle,
            demo_completed_at: new Date().toISOString(),
            user_agent: navigator.userAgent,
            metadata: {
              questions_answered: questions.length,
              total_recording_time: recordingTime
            }
          });
        } catch (error) {
          console.error('Error saving demo lead:', error);
        }
        
        setTimeout(() => setStep('results'), 3000);
      }, 500);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const demoResults = {
    strengths: [
      { name: 'Strategic Thinking', score: 95 },
      { name: 'Communication', score: 92 },
      { name: 'Leadership', score: 88 }
    ],
    workStyle: 'Collaborative and detail-oriented with a focus on innovative solutions',
    topThemes: ['Problem Solver', 'Team Player', 'Results-Driven']
  };

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
      <div className="bg-white rounded-[24px] max-w-3xl w-full shadow-2xl my-8">
        {step === 'intro' && (
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-[#E0EBE9] rounded-full mb-4">
                <Play className="w-8 h-8 text-[#8CAFA8]" />
              </div>
              <h2 className="text-3xl font-semibold text-[#1F2937] mb-4">
                Try Mentra Interactive Demo
              </h2>
              <p className="text-lg text-[#6B7280] max-w-2xl mx-auto">
                Experience our AI-powered feedback process in just 4 minutes. 
                Answer three questions with your voice and see how we transform feedback into insights.
              </p>
            </div>

            <div className="bg-[#F0F4F3] border border-blue-200 rounded-[16px] p-6 mb-8">
              <h3 className="font-semibold text-[#1F2937] mb-4">What you will do:</h3>
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#8CAFA8] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[#4B5563] font-medium">Share your information</p>
                    <p className="text-sm text-[#6B7280]">Quick form to personalize your demo</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#8CAFA8] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[#4B5563] font-medium">Answer 3 questions via voice</p>
                    <p className="text-sm text-[#6B7280]">Role-play as a colleague giving feedback</p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-5 h-5 text-[#8CAFA8] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="text-[#4B5563] font-medium">See your demo profile</p>
                    <p className="text-sm text-[#6B7280]">AI-generated insights and strengths</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-[#4B5563] rounded-lg font-semibold hover:bg-gray-50 transition"
              >
                Maybe Later
              </button>
              <button
                onClick={() => setStep('form')}
                className="flex-1 bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition flex items-center justify-center gap-2"
              >
                Start Demo
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}

        {step === 'form' && (
          <div className="p-8">
            <div className="mb-6">
              <h2 className="text-2xl font-semibold text-[#1F2937] mb-2">
                Tell us about yourself
              </h2>
              <p className="text-[#6B7280]">
                This helps us personalize your demo experience
              </p>
            </div>

            <form onSubmit={handleFormSubmit} className="space-y-5">
              <div>
                <label className="block text-sm font-medium text-[#4B5563] mb-2">
                  Your Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent"
                  placeholder="John Doe"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4B5563] mb-2">
                  Email Address *
                </label>
                <input
                  type="text"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent"
                  placeholder="john@company.com"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4B5563] mb-2">
                  Company Name *
                </label>
                <input
                  type="text"
                  value={formData.company}
                  onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent"
                  placeholder="Acme Corp"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-[#4B5563] mb-2">
                  Your Job Title *
                </label>
                <input
                  type="text"
                  value={formData.jobTitle}
                  onChange={(e) => setFormData({ ...formData, jobTitle: e.target.value })}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg focus:ring-2 focus:ring-[#8CAFA8] focus:border-transparent"
                  placeholder="Product Manager"
                  required
                />
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={() => setStep('intro')}
                  className="flex-1 px-6 py-3 border-2 border-gray-200 text-[#4B5563] rounded-lg font-semibold hover:bg-gray-50 transition"
                >
                  Back
                </button>
                <button
                  type="submit"
                  className="flex-1 bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition"
                >
                  Continue
                </button>
              </div>
            </form>
          </div>
        )}

        {step === 'recording' && (
          <div className="p-8">
            <div className="mb-6">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-semibold text-[#1F2937]">
                  Question {currentQuestion + 1} of {questions.length}
                </h2>
                <span className="text-sm text-[#6B7280]">
                  ~1 min per question
                </span>
              </div>
              <div className="bg-[#F0F4F3] border border-blue-200 rounded-[16px] p-6">
                <p className="text-lg text-[#1F2937] leading-relaxed">
                  {questions[currentQuestion]}
                </p>
              </div>
            </div>

            <div className="bg-gray-50 rounded-[16px] p-8 text-center mb-6">
              {!isRecording ? (
                <div>
                  <p className="text-[#6B7280] mb-6">
                    Click the microphone when you are ready to answer
                  </p>
                  <button
                    onClick={startRecording}
                    className="inline-flex items-center justify-center w-20 h-20 bg-red-600 text-white rounded-full hover:bg-red-700 transition shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
                  >
                    <Mic className="w-10 h-10" />
                  </button>
                  <p className="text-sm text-[#9CA3AF] mt-4">
                    Enable microphone access when prompted
                  </p>
                </div>
              ) : (
                <div>
                  <div className="inline-flex items-center justify-center w-20 h-20 bg-red-600 text-white rounded-full mb-4 animate-pulse">
                    <Mic className="w-10 h-10" />
                  </div>
                  <div className="text-4xl font-semibold text-[#1F2937] mb-2">
                    {formatTime(recordingTime)}
                  </div>
                  <p className="text-[#6B7280] mb-6">Recording in progress...</p>
                  <button
                    onClick={stopRecording}
                    className="bg-gray-800 text-white px-8 py-3 rounded-lg font-semibold hover:bg-gray-900 transition"
                  >
                    Stop & Continue
                  </button>
                </div>
              )}
            </div>

            <div className="flex justify-center gap-2">
              {questions.map((_, idx) => (
                <div
                  key={idx}
                  className={`h-2 w-12 rounded-full transition ${
                    idx < currentQuestion
                      ? 'bg-green-600'
                      : idx === currentQuestion
                      ? 'bg-[#8CAFA8]'
                      : 'bg-gray-300'
                  }`}
                />
              ))}
            </div>
          </div>
        )}

        {step === 'processing' && (
          <div className="p-8 text-center">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-[#E0EBE9] rounded-full mb-6">
              <Sparkles className="w-10 h-10 text-[#8CAFA8] animate-pulse" />
            </div>
            <h2 className="text-3xl font-semibold text-[#1F2937] mb-4">
              Analyzing Your Responses...
            </h2>
            <p className="text-lg text-[#6B7280] mb-8">
              Our AI is processing your answers and generating personalized insights
            </p>
            <div className="max-w-md mx-auto">
              <div className="bg-gray-200 rounded-full h-2 mb-4">
                <div className="bg-[#8CAFA8] h-2 rounded-full animate-pulse" style={{ width: '75%' }}></div>
              </div>
              <div className="space-y-2 text-sm text-[#6B7280]">
                <div className="flex items-center justify-center gap-2">
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Transcribing voice responses...</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {step === 'results' && (
          <div className="p-8">
            <div className="text-center mb-8">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                <CheckCircle2 className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-3xl font-semibold text-[#1F2937] mb-2">
                Your Demo Profile is Ready!
              </h2>
              <p className="text-[#6B7280]">
                Here is what Mentra discovered about {formData.name}
              </p>
            </div>

            <div className="space-y-6 mb-8">
              <div className="bg-gradient-to-br from-[#8CAFA8] to-[#7A9B94] text-white rounded-[16px] p-6">
                <h3 className="font-semibold mb-2">Work Style</h3>
                <p className="text-blue-100">{demoResults.workStyle}</p>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-[16px] p-6">
                <h3 className="font-semibold text-[#1F2937] mb-4">Top Strengths</h3>
                <div className="space-y-4">
                  {demoResults.strengths.map((strength, idx) => (
                    <div key={idx}>
                      <div className="flex items-center justify-between mb-2">
                        <span className="font-medium text-[#1F2937]">{strength.name}</span>
                        <span className="text-sm font-semibold text-[#8CAFA8]">{strength.score}%</span>
                      </div>
                      <div className="bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-[#8CAFA8] h-2 rounded-full transition-all duration-1000"
                          style={{ width: `${strength.score}%` }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-white border-2 border-gray-200 rounded-[16px] p-6">
                <h3 className="font-semibold text-[#1F2937] mb-4">Key Themes</h3>
                <div className="flex flex-wrap gap-2">
                  {demoResults.topThemes.map((theme, idx) => (
                    <span
                      key={idx}
                      className="bg-[#E0EBE9] text-[#7A9B94] px-4 py-2 rounded-lg font-medium"
                    >
                      {theme}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-[16px] p-6 mb-6">
              <h3 className="font-semibold text-green-900 mb-2">
                Experience the Full Power of Mentra
              </h3>
              <p className="text-green-800 mb-4">
                This was just a taste! Get real feedback from your colleagues and unlock deeper insights about your professional strengths.
              </p>
              <ul className="space-y-2 text-sm text-green-800">
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Collect feedback from unlimited colleagues</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Get comprehensive AI analysis from multiple perspectives</span>
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" />
                  <span>Track your growth over time with analytics</span>
                </li>
              </ul>
            </div>

            <div className="flex gap-4">
              <button
                onClick={onClose}
                className="flex-1 px-6 py-3 border-2 border-gray-200 text-[#4B5563] rounded-lg font-semibold hover:bg-gray-50 transition"
              >
                Close Demo
              </button>
              <button
                onClick={() => window.location.href = '/auth'}
                className="flex-1 bg-[#8CAFA8] text-white px-6 py-3 rounded-lg font-semibold hover:bg-[#7A9B94] transition flex items-center justify-center gap-2"
              >
                Start Free Trial
                <ArrowRight className="w-5 h-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
